import React from 'react';
import type { TapChangeLogEntry } from '../api/types';

interface TapHistoryTableProps {
  items: TapChangeLogEntry[];
  showLocationColumn: boolean;
  isLoading: boolean;
}

const CHANGE_TYPE_LABEL: Record<TapChangeLogEntry['changeType'], string> = {
  kicked:   'KICKED',
  tapped:   'TAPPED',
  replaced: 'REPLACED',
};

const CHANGE_TYPE_COLOR: Record<TapChangeLogEntry['changeType'], string> = {
  kicked:   'var(--text-muted)',
  tapped:   'var(--text)',
  replaced: 'var(--text)',
};

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yest  = new Date(today);
  yest.setDate(yest.getDate() - 1);
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (d.toDateString() === today.toDateString()) return `Today, ${time}`;
  if (d.toDateString() === yest.toDateString())  return `Yesterday, ${time}`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) + ', ' + time;
}

export function TapHistoryTable({
  items,
  showLocationColumn,
  isLoading,
}: TapHistoryTableProps): React.ReactElement {
  if (isLoading) {
    return (
      <div className="panel-skeleton" aria-label="Loading" aria-busy="true">
        {[1, 2, 3, 4, 5].map((i) => <div key={i} className="skeleton-row" />)}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <p className="panel-empty text-sm text-muted">
        No tap changes recorded yet.
      </p>
    );
  }

  const thStyle: React.CSSProperties = {
    padding: '8px 12px',
    textAlign: 'left',
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--text-muted)',
    borderBottom: '1px solid var(--border)',
    whiteSpace: 'nowrap',
  };

  const tdStyle: React.CSSProperties = {
    padding: '10px 12px',
    borderBottom: '1px solid var(--border)',
    fontSize: '0.875rem',
    verticalAlign: 'middle',
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {showLocationColumn && <th style={thStyle}>Location</th>}
            <th style={thStyle}>Tap</th>
            <th style={thStyle}>Previous Beer</th>
            <th style={thStyle}>New Beer</th>
            <th style={thStyle}>Date / Time</th>
            <th style={thStyle}>Type</th>
          </tr>
        </thead>
        <tbody>
          {items.map((entry) => (
            <tr key={entry.id}>
              {showLocationColumn && (
                <td style={tdStyle}>{entry.locationName ?? '—'}</td>
              )}
              <td style={{ ...tdStyle, fontWeight: 700 }} className="mono">
                {entry.tapNumber}
              </td>
              <td style={{ ...tdStyle, color: entry.previousBeerName ? 'var(--text)' : 'var(--text-subtle)' }}>
                {entry.previousBeerName ?? '—'}
              </td>
              <td style={{ ...tdStyle, color: entry.newBeerName ? 'var(--text)' : 'var(--text-subtle)' }}>
                {entry.newBeerName ?? '—'}
              </td>
              <td style={{ ...tdStyle, color: 'var(--text-muted)' }} className="mono">
                {formatDateTime(entry.changedAt)}
              </td>
              <td style={tdStyle}>
                <span style={{
                  display: 'inline-block',
                  padding: '2px 8px',
                  borderRadius: 4,
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  border: '1px solid var(--border)',
                  color: CHANGE_TYPE_COLOR[entry.changeType],
                }}>
                  {CHANGE_TYPE_LABEL[entry.changeType] ?? entry.changeType.toUpperCase()}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
